import type React from 'react';

// Enter/Space activation for targets that cannot be a native <button>. Prefer a real
// <button> or <a href>; use this only where the tag is fixed by layout or table
// semantics, paired with tabIndex={0} and a focus ring from focusRing.ts.
// `keys: 'enter'` drops Space for targets that read as content rather than as a button
// — swallowing Space on a table row would take page scrolling away while it holds focus.
export const activateOnEnterOrSpace = (
    event: React.KeyboardEvent,
    activate: () => void,
    keys: 'enter-or-space' | 'enter' = 'enter-or-space',
): void => {
    const activates = event.key === 'Enter' || (keys === 'enter-or-space' && event.key === ' ');
    if (!activates) {
        return;
    }
    // Keydown bubbles: without this a nested control activates its ancestor row too.
    if (event.target !== event.currentTarget) {
        return;
    }
    // Space scrolls the nearest container; Enter submits a wrapping form.
    event.preventDefault();
    event.stopPropagation();
    activate();
};

// Named controls only. A bare `[tabindex]` arm would also catch focusable-but-not-
// actionable content (a scroll container, a copyable block) and silently eat row clicks.
const INTERACTIVE_SELECTOR =
    'button, a[href], input, select, textarea, [role="button"], [role="link"]';

// Enter on a nested <button> fires a click that bubbles, so a clickable row activates
// alongside it. Looser than the keydown guard above: a click on plain cell content must
// still activate the row, so only bail on a real control.
export const isFromInteractiveDescendant = (event: React.MouseEvent): boolean => {
    const target = event.target as HTMLElement | null;
    if (!target?.closest) {
        return false;
    }
    const control = target.closest(INTERACTIVE_SELECTOR);
    return !!control && control !== event.currentTarget;
};
